import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getKidSub } from '@/auth/authStore';
import type { StudyDocumentDetail, StudyPdfAnnotation, StudyPdfAnnotationInput } from './studyApi';
import {
  listImageAnnotations,
  removeImageAnnotation,
  saveImageAnnotation,
} from './studyImageAnnotationsApi';
import { rectangle, type PdfPoint } from './studyPdfAnnotationGeometry';

type Tool = 'read' | 'ink' | 'highlight';
type Mark =
  | { kind: 'ink'; points: PdfPoint[] }
  | { kind: 'highlight'; start: PdfPoint; end: PdfPoint };
const inkColour = 'var(--ink)';
const highlightColour = 'var(--brand-sunshine)';

/** Coordinates belong to the original image, independent of screen size or zoom. */
export function StudyImageAnnotations({
  document,
  onUnsavedChange,
}: {
  document: StudyDocumentDetail;
  onUnsavedChange: (dirty: boolean) => void;
}) {
  const owner = getKidSub();
  const live = useRef(true);
  const client = useQueryClient();
  const key = ['image-annotations', owner, document.id, document.source_revision];
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const items: StudyPdfAnnotation[] = [];
      let cursor: string | undefined;
      const seen = new Set<string>();
      do {
        const page = await listImageAnnotations(document.id, cursor);
        items.push(...page.items);
        cursor = page.next_cursor ?? undefined;
        if (cursor && (seen.has(cursor) || seen.size >= 100))
          throw new Error('Annotations could not be loaded completely. Retry loading them.');
        if (cursor) seen.add(cursor);
      } while (cursor);
      return items;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [tool, setTool] = useState<Tool>('read');
  const [mark, setMark] = useState<Mark | null>(null);
  const currentMark = useRef<Mark | null>(null);
  const pointer = useRef<number | null>(null);
  const [pending, setPending] = useState<StudyPdfAnnotationInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('Read, draw or highlight your original.');
  const [zoom, setZoom] = useState(1);
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    onUnsavedChange(Boolean(mark || pending || saving || deleting));
  }, [mark, pending, saving, deleting, onUnsavedChange]);
  const stillHere = () => live.current && getKidSub() === owner;
  const unavailable = query.isError || !query.isSuccess || !imageReady || imageFailed;
  function point(event: PointerEvent<SVGSVGElement>): PdfPoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
    };
  }
  async function save(input: StudyPdfAnnotationInput) {
    setPending(input);
    setSaving(true);
    setError(null);
    try {
      const saved = await saveImageAnnotation(document.id, input);
      if (!stillHere()) return;
      client.setQueryData<StudyPdfAnnotation[]>(key, (items = []) => [
        saved,
        ...items.filter((i) => i.id !== saved.id),
      ]);
      setPending(null);
      setFeedback('Annotation saved.');
    } catch (reason) {
      if (stillHere())
        setError(
          reason instanceof Error
            ? reason.message
            : 'Annotation could not be saved. Retry this change.',
        );
    } finally {
      if (stillHere()) setSaving(false);
    }
  }
  function start(event: PointerEvent<SVGSVGElement>) {
    if (
      tool === 'read' ||
      unavailable ||
      pending ||
      saving ||
      deleting ||
      event.isPrimary === false ||
      event.button > 0
    )
      return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointer.current = event.pointerId;
    const at = point(event);
    const next: Mark =
      tool === 'ink' ? { kind: 'ink', points: [at] } : { kind: 'highlight', start: at, end: at };
    currentMark.current = next;
    setMark(next);
  }
  function move(event: PointerEvent<SVGSVGElement>) {
    const draft = currentMark.current;
    if (!draft || pointer.current !== event.pointerId) return;
    const at = point(event);
    const next: Mark =
      draft.kind === 'ink'
        ? { ...draft, points: [...draft.points.slice(0, 1498), at] }
        : { ...draft, end: at };
    currentMark.current = next;
    setMark(next);
  }
  function finish(event: PointerEvent<SVGSVGElement>) {
    const draft = currentMark.current;
    if (!draft || pointer.current !== event.pointerId) return;
    const at = point(event);
    currentMark.current = null;
    pointer.current = null;
    setMark(null);
    const base = {
      request_id: crypto.randomUUID(),
      page_number: 1,
      source_revision: document.source_revision,
    };
    if (draft.kind === 'ink')
      void save({
        ...base,
        kind: 'ink',
        geometry: {
          points: [...draft.points.slice(0, 1499), at],
          colour: 'ink',
          width: 0.004,
        },
      });
    else {
      const rect = rectangle(draft.start, at);
      if (rect.width > 0 && rect.height > 0)
        void save({ ...base, kind: 'highlight', geometry: { ...rect, colour: 'yellow' } });
    }
  }
  function cancel() {
    pointer.current = null;
    currentMark.current = null;
    setMark(null);
  }
  async function remove(item: StudyPdfAnnotation) {
    setDeleting(true);
    setError(null);
    try {
      await removeImageAnnotation(document.id, item);
      if (stillHere()) {
        client.setQueryData<StudyPdfAnnotation[]>(key, (items) =>
          items?.filter((i) => i.id !== item.id),
        );
        setFeedback('Annotation removed.');
      }
    } catch (reason) {
      if (stillHere())
        setError(
          reason instanceof Error
            ? reason.message
            : 'Delete failed. Reload saved annotations before retrying.',
        );
    } finally {
      if (stillHere()) setDeleting(false);
    }
  }
  const items = query.isError ? [] : (query.data ?? []);
  return (
    <div className="flex min-h-0 min-w-0 flex-col lg:h-full" aria-label="Original notes image">
      <div
        className="flex shrink-0 flex-wrap gap-2 border-b border-hairline bg-canvas-pure p-3"
        aria-label="Image annotation tools"
      >
        {(['read', 'ink', 'highlight'] as const).map((t) => (
          <button
            key={t}
            type="button"
            aria-pressed={tool === t}
            disabled={unavailable || Boolean(pending || mark) || saving || deleting}
            className="btn-pill-secondary px-4"
            onClick={() => setTool(t)}
          >
            {t === 'ink' ? 'Draw' : t === 'read' ? 'Read' : 'Highlight'}
          </button>
        ))}
        <button
          type="button"
          className="btn-pill-secondary px-4"
          disabled={zoom <= 1 || Boolean(mark)}
          onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
          aria-label="Zoom out image"
        >
          −
        </button>
        <span className="self-center text-sm font-bold text-ink">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          className="btn-pill-secondary px-4"
          disabled={zoom >= 3 || Boolean(mark)}
          onClick={() => setZoom((z) => Math.min(3, z + 0.5))}
          aria-label="Zoom in image"
        >
          +
        </button>
      </div>
      <div className="min-h-48 min-w-0 overflow-auto p-4 lg:min-h-0 lg:flex-1">
        <div className="relative mx-auto" style={{ width: `${zoom * 100}%` }}>
          <img
            src={document.read_url}
            alt={document.file_name}
            className="block h-auto w-full rounded-2xl"
            onLoad={() => {
              setImageReady(true);
              setImageFailed(false);
            }}
            onError={() => setImageFailed(true)}
          />
          {imageReady && !imageFailed && (
            <svg
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              aria-label="Image annotation layer"
              className={`absolute inset-0 h-full w-full ${tool === 'read' ? 'pointer-events-none' : 'touch-none cursor-crosshair'}`}
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={finish}
              onPointerCancel={cancel}
            >
              {items.map((item) =>
                item.kind === 'ink' ? (
                  <polyline
                    key={item.id}
                    data-annotation-id={item.id}
                    points={(item.geometry.points as PdfPoint[])
                      .map((p) => `${p.x},${p.y}`)
                      .join(' ')}
                    fill="none"
                    stroke={inkColour}
                    strokeWidth={Number(item.geometry.width)}
                    strokeLinecap="round"
                  />
                ) : item.kind === 'highlight' ? (
                  <rect
                    key={item.id}
                    data-annotation-id={item.id}
                    x={Number(item.geometry.x)}
                    y={Number(item.geometry.y)}
                    width={Number(item.geometry.width)}
                    height={Number(item.geometry.height)}
                    fill={highlightColour}
                    fillOpacity={0.36}
                  />
                ) : null,
              )}
              {mark?.kind === 'ink' && (
                <polyline
                  points={mark.points.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={inkColour}
                  strokeWidth={0.004}
                />
              )}
              {mark?.kind === 'highlight' && (
                <rect
                  {...rectangle(mark.start, mark.end)}
                  fill={highlightColour}
                  fillOpacity={0.36}
                />
              )}
              {pending?.kind === 'ink' && (
                <polyline
                  points={pending.geometry.points.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={inkColour}
                  strokeWidth={0.004}
                />
              )}
              {pending?.kind === 'highlight' && (
                <rect
                  x={pending.geometry.x}
                  y={pending.geometry.y}
                  width={pending.geometry.width}
                  height={pending.geometry.height}
                  fill={highlightColour}
                  fillOpacity={0.36}
                />
              )}
            </svg>
          )}
        </div>
      </div>
      <div className="shrink-0 border-t border-hairline bg-canvas-pure p-3 text-sm text-ink">
        <p>Private annotations. Your original image stays unchanged.</p>
        {imageFailed && (
          <p role="alert">The original image could not be displayed. Reopen the material.</p>
        )}
        {query.isError && (
          <p role="alert">
            Annotations could not be loaded.{' '}
            <button className="min-h-11 font-bold underline" onClick={() => void query.refetch()}>
              Reload annotations
            </button>
          </p>
        )}
        {error && <p role="alert">{error}</p>}
        {pending && (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={saving}
              className="btn-pill-secondary"
              onClick={() => void save(pending)}
            >
              Retry annotation save
            </button>
            <button
              type="button"
              disabled={saving}
              className="btn-pill-secondary"
              onClick={() => {
                setPending(null);
                setError(null);
              }}
            >
              Discard unsaved annotation
            </button>
          </div>
        )}
        <details>
          <summary className="min-h-11 cursor-pointer py-3 font-bold">
            Saved annotations ({items.length})
          </summary>
          {items.map((item, i) => (
            <div
              className="flex items-center justify-between border-t border-hairline"
              key={item.id}
            >
              <span>
                {i + 1}.{' '}
                {item.kind === 'ink' ? 'Drawing' : item.kind === 'highlight' ? 'Highlight' : 'Note'}
              </span>
              <button
                type="button"
                className="min-h-11 font-bold text-brand-coral"
                disabled={saving || deleting || Boolean(pending || mark)}
                onClick={() => void remove(item)}
                aria-label={`Delete annotation ${i + 1}`}
              >
                Delete
              </button>
            </div>
          ))}
        </details>
        <p role="status">
          {saving
            ? 'Saving annotation…'
            : deleting
              ? 'Deleting annotation…'
              : pending
                ? 'Annotation not saved.'
                : feedback}
        </p>
      </div>
    </div>
  );
}
